/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { subscribe, getSession, isOffline, getAuthEvent, call, ApiError, type Body } from './api';
import { CAPABILITY_OFF_NO } from './capability-off';
import { toast } from './toast';

export function useSession() {
  return useSyncExternalStore(subscribe, getSession);
}
export function useOffline() {
  return useSyncExternalStore(subscribe, isOffline);
}
export function useAuthEvent() {
  return useSyncExternalStore(subscribe, getAuthEvent);
}

/**
 * Load a GET endpoint for a view. Each view loads on its own, so one endpoint
 * failing does not blank the rest of the panel (the rule the prototype's
 * independent painters kept). `reload()` repaints after a write.
 */
export function useLoad<T = Body>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const seq = useRef(0);
  const reload = useCallback(async () => {
    if (!path) return;
    const n = ++seq.current;
    setLoading(true);
    try {
      const d = await call<T>(path);
      if (n === seq.current) { setData(d); setError(null); }
    } catch (e) {
      // A switched-off function is not a failed load (PM gaps B1): the one sentence LoadError knows, with no «Prøv igjen».
      if (n === seq.current) setError(e instanceof ApiError && e.body && e.body.code === 'capability_disabled' ? CAPABILITY_OFF_NO : (e as Error).message);
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, [path]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [reload, ...deps]);
  return { data, error, loading, reload, setData };
}

/**
 * A message line: [text, tone]. Since 2026-10-31 every `say` is a toast (lib/toast.ts) — seen wherever the page is
 * scrolled — and `<Msg>` draws nothing. Each `useMsg()` is one toast source: its next message replaces its last.
 * `msg` still holds the last words, for the views that look at them.
 */
export type Tone = 'ok' | 'err' | 'warn' | '';
let msgSources = 0;
export function useMsg() {
  const [msg, setMsg] = useState<{ text: string; tone: Tone }>({ text: '', tone: '' });
  const source = useRef('');
  if (!source.current) source.current = 'msg-' + (++msgSources);
  const say = useCallback((text: string, tone: Tone = '') => { toast(text, tone, { source: source.current }); setMsg({ text, tone }); }, []);
  return [msg, say] as const;
}

/** For a message kept in a view's own state: a toast each time it changes to new words. */
export function useToastOn(text: string | null | undefined, tone: Tone = '') {
  useEffect(() => { if (text) toast(text, tone); }, [text, tone]);
}

export const isSet = (v: any) => v !== null && v !== undefined && v !== '';
export { dropChanged } from './field-errors';

/**
 * One "unsaved" flag per form, for a view with several forms (QA C, 2026-10-16). Påmelding had ONE flag that every form
 * wrote: the settings form, reporting "clean", cleared the flag a half-typed new entry had set, so leaving the page lost
 * the entry without a question. `setFlag` is the pure part (the same map back when nothing changes).
 */
export function setFlag(flags: Record<string, boolean>, key: string, value: boolean): Record<string, boolean> {
  if (Boolean(flags[key]) === value) return flags;
  const next = { ...flags };
  if (value) next[key] = true; else delete next[key];
  return next;
}
export const anyFlag = (flags: Record<string, boolean>): boolean => Object.values(flags).some(Boolean);

/** `mark(key)` is a stable setter for that form's flag (safe in an effect's deps); `any` is true while one is set. */
export function useFlags() {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const setters = useRef(new Map<string, (d: boolean) => void>());
  const mark = useCallback((key: string) => {
    let f = setters.current.get(key);
    if (!f) { f = (d: boolean) => setFlags((x) => setFlag(x, key, d)); setters.current.set(key, f); }
    return f;
  }, []);
  return { any: anyFlag(flags), mark };
}
